import React, { Fragment, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import DownloadFileModal from '../../components/common/download-file-modal';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi from '../../utils/henceforthApi';
import moment from 'moment';
import BreadCrumb from '../../components/common/BreadCrumb';
import { numberWithCommas } from '../../utils/validations';
import { toast } from 'react-toastify';
import AgTable from '../../components/common/AgTable';
import { ColDef } from 'ag-grid-community';
import defaultImage from '../../assets/images/pages/defaultImage.jpg';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  Search,
  X,
  Download,
  Copy,
  Check,
  DollarSign,
  Calendar as CalendarIcon,
  RotateCcw,
  ChevronDown,
  Eye,
  Truck,
} from 'lucide-react';
import henceofrthEnums from '../../utils/henceofrthEnums';
import SelectDropdown, { SelectDropdownOption } from '../../components/ui/select-dropdown';
import { getStatusBadge } from '../../utils/orderStatusBadge';
import { Button } from '../../components/ui/button';

const OrderListing: React.FC = () => {
  const Limit = 5;
  const location = useLocation();
  const navigate = useNavigate();
  const match = useMatch('/orders/:page');
  const { authState, loading, setLoading, language_data, authDispatch, handleSearch, onFilterPriceHandler } =
    useContext(GlobalContext);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_order || 'Orders'} ${language_data?.[authState.lang]?.common_list || 'List'
        }`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const newParam = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [orderL, setOrderL] = useState<{ total_count: number; data: any[] }>({
    total_count: 0,
    data: [],
  });

  // Local Search state
  const [searchTerm, setSearchTerm] = useState(newParam.get('search') || '');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Price popover state
  const [showPricePopover, setShowPricePopover] = useState(false);
  const [minPriceInput, setMinPriceInput] = useState(newParam.get('min_price') || '');
  const [maxPriceInput, setMaxPriceInput] = useState(newParam.get('max_price') || '');
  const pricePopoverRef = useRef<HTMLDivElement>(null);

  // Date popover state
  const [showDatePopover, setShowDatePopover] = useState(false);
  const datePopoverRef = useRef<HTMLDivElement>(null);

  // Sync inputs with URL params
  useEffect(() => {
    setSearchTerm(newParam.get('search') || '');
    setMinPriceInput(newParam.get('min_price') || '');
    setMaxPriceInput(newParam.get('max_price') || '');
  }, [newParam]);

  // Click outside listeners
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pricePopoverRef.current && !pricePopoverRef.current.contains(e.target as Node)) {
        setShowPricePopover(false);
      }
      if (datePopoverRef.current && !datePopoverRef.current.contains(e.target as Node)) {
        setShowDatePopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const orderListing = async () => {
    setLoading(true);
    try {
      const pageNum = Number(match?.params.page) || 1;
      const res = (
        await henceforthApi.Order.getOrderList(pageNum - 1, Limit, newParam.toString())
      ).data;
      setOrderL({
        total_count: res.total_count || 0,
        data: res.data || [],
      });
    } catch (err: any) {
      console.log('err', err?.response?.body?.error_description);
      setOrderL({
        total_count: 0,
        data: [],
      });
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    orderListing();
  }, [
    match?.params.page,
    newParam.get('search'),
    newParam.get('stock'),
    newParam.get('start_date'),
    newParam.get('end_date'),
    newParam.get('payment_status'),
    newParam.get('order_status'),
    newParam.get('product_id'),
    newParam.get('min_price'),
    newParam.get('max_price'),
  ]);

  const copyText = useCallback((id: string, name: string) => {
    if (id) {
      navigator?.clipboard?.writeText(id);
      setCopiedId(id);
      toast.success(
        `${name} ${language_data?.[authState.lang]?.common_copy || 'copied'} ${language_data?.[authState.lang]?.common_successfull || 'successfully'
        }`
      );
      setTimeout(() => {
        setCopiedId((curr) => (curr === id ? null : curr));
      }, 2000);
    }
  }, [authState, language_data]);

  const exportData = useCallback(async (startDate: number, endDate: number) => {
    try {
      const apiRes = await henceforthApi.Order.export(startDate, endDate, '&order_status=ALL');
      const data = apiRes.data.data;

      const rows = [
        [
          'Order ID',
          'Customer Detail',
          'Seller Detail',
          'Product ID',
          'Product Name',
          'Order Price',
          'Coupon/Discount',
          'Order Status',
          'Earning',
        ],
      ];

      if (Array.isArray(data) && data.length > 0) {
        data.forEach((res: any) => {
          const product = res?.products?.[0] || res?.product;
          rows.push([
            res?.order_id || '',
            res?.user?.name || '',
            res?.seller?.name || '',
            product?.prodct_id || product?.product_id || '',
            product?.name || '',
            res?.total_price ? numberWithCommas(res.total_price) : '0',
            res?.coupon_discount || '0',
            res?.order_status || '',
            res?.total_earnings ? numberWithCommas(res.total_earnings) : '0',
          ]);
        });
      }

      const csvContent = 'data:text/csv;charset=utf-8,' + rows.map((e) => e.join(',')).join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.href = encodedUri;
      link.setAttribute('download', `orders_${moment().valueOf()}.csv`);
      document.body.appendChild(link);
      link.click();

      const closeModal = document.getElementById('closeModal');
      if (closeModal) {
        closeModal.click();
      }
    } catch (err: any) {
      console.error('Export Error:', err);
      handleError(err, 'active', authDispatch);
    }
  }, [authDispatch]);

  // Search Submit & Clear
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch('search', searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    handleSearch('search', '');
  };

  // Status Tab selection
  const currentStatus = newParam.get('order_status') || '';
  const currentPaymentStatus = newParam.get('payment_status') || '';

  const handleStatusFilter = (statusVal: string, isPayment = false) => {
    if (isPayment) {
      handleSearch('payment_status', statusVal);
    } else {
      handleSearch('order_status', statusVal);
    }
  };

  // Price filter
  const hasPrice = newParam.has('min_price') && newParam.has('max_price');
  const handleApplyPrice = (e: React.FormEvent) => {
    e.preventDefault();
    if (Number(minPriceInput) > Number(maxPriceInput)) {
      return toast.warn(
        `${language_data?.[authState.lang]?.common_max || 'Max'} ${language_data?.[authState.lang]?.common_price || 'price'
        } ${language_data?.[authState.lang]?.common_should_be_greater_than || 'should be greater than'
        } ${language_data?.[authState.lang]?.common_min || 'min'} ${language_data?.[authState.lang]?.common_price || 'price'
        }`
      );
    }
    onFilterPriceHandler(minPriceInput, maxPriceInput);
    setShowPricePopover(false);
  };

  const handleClearPrice = () => {
    setMinPriceInput('');
    setMaxPriceInput('');
    onFilterPriceHandler('', '');
    setShowPricePopover(false);
  };

  // Date filter
  const hasDate = newParam.has('start_date') && newParam.has('end_date');
  const dateSelect = (e: any) => {
    handleSearch('start_date', e[0] ? String(moment(e[0]).valueOf()) : '');
    handleSearch('end_date', e[1] ? String(moment(e[1]).valueOf()) : '');
    setShowDatePopover(false);
  };

  const handleClearDate = () => {
    handleSearch('start_date', '');
    setShowDatePopover(false);
  };

  // Clear all filters
  const isAnyFilterActive =
    Boolean(newParam.get('search')) ||
    Boolean(newParam.get('order_status')) ||
    Boolean(newParam.get('payment_status')) ||
    Boolean(newParam.get('stock')) ||
    hasPrice ||
    hasDate;

  const handleResetAllFilters = () => {
    setSearchTerm('');
    setMinPriceInput('');
    setMaxPriceInput('');
    navigate('/orders/1');
  };


  // AG-Grid Columns Definition
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: `${language_data?.[authState.lang]?.common_sr_no || 'Sr.No.'}`,
        field: 'sr_no',
        width: 80,
        minWidth: 70,
        valueGetter: (params) => {
          const pageNum = Number(match?.params.page) || 1;
          return (pageNum - 1) * Limit + (params.node?.rowIndex ?? 0) + 1;
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_order || 'Order'} ${language_data?.[authState.lang]?.common_id || 'ID'
          }`,
        field: 'order_id',
        minWidth: 170,
        cellRenderer: (params: any) => {
          const id =
            params.data?.order_id ||
            `${language_data?.[authState.lang]?.common_not_available || 'N/A'}`;
          const isCopied = copiedId === params.data?.order_id;
          return (
            <div className="flex items-center gap-2 h-full">
              <span className="font-bold text-[#1AA7DE] tracking-tight">{id}</span>
              {params.data?.order_id && (
                <button
                  type="button"
                  onClick={() => copyText(params.data.order_id, 'Order ID')}
                  className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
                  title="Copy Order ID"
                >
                  {isCopied ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                  )}
                </button>
              )}
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_customer || 'Customer'}`,
        field: 'user.name',
        minWidth: 190,
        cellRenderer: (params: any) => {
          const pic = params.data?.user?.profile_pic
            ? `${henceforthApi.API_FILE_ROOT_SMALL}${params.data.user.profile_pic}`
            : defaultImage;
          const name =
            params.data?.user?.name ||
            language_data?.[authState.lang]?.common_not_available ||
            'Customer';
          const email = params.data?.user?.email;
          return (
            <div className="flex items-center gap-2.5 h-full py-1">
              <img
                src={pic}
                alt={name}
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
              />
              <div className="flex flex-col justify-center overflow-hidden">
                <span className="font-semibold text-slate-800 text-xs sm:text-sm truncate">
                  {name}
                </span>
                {email && (
                  <span className="text-2xs text-slate-400 font-medium truncate" title={email}>
                    {email}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_product || 'Product'}`,
        field: 'products',
        minWidth: 240,
        cellRenderer: (params: any) => {
          const firstProduct = params.data?.products?.[0];
          const pic = firstProduct?.images?.[0]
            ? `${henceforthApi.API_FILE_ROOT_SMALL}${firstProduct.images[0]}`
            : defaultImage;
          const category = params.data?.category_name || '';
          const title = firstProduct?.name || 'Product Item';
          return (
            <div className="flex items-center gap-3 h-full py-1">
              <img
                src={pic}
                alt={title}
                className="w-9 h-9 rounded-lg object-cover border border-slate-200 shrink-0"
              />
              <div className="flex flex-col justify-center overflow-hidden">
                {category && (
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {category}
                  </span>
                )}
                <span className="text-xs font-semibold text-slate-800 truncate" title={title}>
                  {title}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_quantity || 'Qty'}`,
        field: 'order_quantity',
        width: 85,
        minWidth: 80,
        cellRenderer: (params: any) => (
          <div className="flex items-center h-full">
            <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-md bg-slate-100 font-bold text-xs text-slate-700">
              {params.data?.order_quantity ?? 0}
            </span>
          </div>
        ),
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_delivery_type || 'Delivery'}`,
        field: 'delivery_type',
        minWidth: 125,
        cellRenderer: (params: any) => (
          <div className="flex items-center h-full">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 text-2xs font-bold uppercase tracking-wider">
              <Truck className="w-3 h-3 text-slate-400" />
              {params.data?.delivery_type || 'STANDARD'}
            </span>
          </div>
        ),
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_price || 'Price'}`,
        field: 'total_price',
        minWidth: 110,
        cellRenderer: (params: any) => (
          <div className="flex items-center h-full font-black text-slate-900 text-xs sm:text-sm">
            ${numberWithCommas(params.data?.total_price || 0)}
          </div>
        ),
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_status || 'Status'}`,
        field: 'order_status',
        minWidth: 155,
        cellRenderer: (params: any) => {
          const badge = getStatusBadge(params.data?.order_status);
          return (
            <div className="flex items-center h-full">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${badge.pill}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                {badge.label}
              </span>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_earning || 'Earning'}`,
        field: 'total_earnings',
        minWidth: 110,
        cellRenderer: (params: any) => (
          <div className="flex items-center h-full font-black text-emerald-600 text-xs sm:text-sm">
            ${numberWithCommas(params.data?.total_earnings || 0)}
          </div>
        ),
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_action || 'Action'}`,
        field: '_id',
        width: 105,
        minWidth: 95,
        sortable: false,
        filter: false,
        pinned: 'right',
        cellRenderer: (params: any) => {
          const isBlocked =
            authState?.is_admin_verified === 'pending' ||
            authState?.is_admin_verified === 'reject';
          return (
            <div className="flex items-center h-full">
              <Button variant="default" size="sm" asChild>
                <Link
                  to={isBlocked ? '#' : `/order/${params.data?._id}`}
                  onClick={(e) => {
                    if (authState?.is_admin_verified === 'pending') {
                      e.preventDefault();
                      toast.error('Your account is pending for admin verification.');
                    } else if (authState?.is_admin_verified === 'reject') {
                      e.preventDefault();
                      toast.error('Your account is rejected for admin verification.');
                    }
                  }}
                >
                  <Eye className="w-3.5 h-3.5" />
                </Link>
              </Button>
            </div>
          );
        },
      },
    ],
    [authState, language_data, match?.params.page, copiedId, copyText]
  );

  return (
    <Fragment>
      {/* Standard Breadcrumb Bar */}
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {/* 1. Header Card with Title, Counter Badge & Export Button */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_order || 'Orders'} Catalog
              </h1>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-sky-50 text-[#1AA7DE] border border-sky-200/60 shadow-2xs">
                {orderL.total_count} {orderL.total_count === 1 ? 'Order' : 'Orders'}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
              Track customer orders, fulfillment statuses, delivery methods, and store earnings.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Export CSV Button */}
            <button
              type="button"
              data-bs-toggle="modal"
              data-bs-target="#fileDownloadModal"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs sm:text-sm shadow-2xs transition-all active:scale-[0.98] cursor-pointer"
            >
              <Download className="w-4 h-4 text-slate-500" />
              <span>{language_data?.[authState.lang]?.common_export_file || 'Export CSV'}</span>
            </button>
          </div>
        </div>

        {/* 2. Modern Filter & Search Toolbar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-3.5">

          {/* Search, Date, Price & Reset Controls */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 flex-wrap">
            {/* Search Input */}
            <form onSubmit={handleSearchSubmit} className="relative max-w-[350px]  flex-1 min-w-[260px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by order ID, customer, or product..."
                className="w-full pl-10 pr-9 py-2 text-xs sm:text-sm font-medium bg-slate-50/80 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </form>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Status Dropdown — using reusable SelectDropdown component */}
              {(() => {
                const activeValue =
                  currentPaymentStatus === henceofrthEnums.OrderStatus.REFUNDED
                    ? henceofrthEnums.OrderStatus.REFUNDED
                    : currentStatus || '';

                const statusOptions: SelectDropdownOption[] = [
                  { value: '', label: 'All Statuses', subLabel: 'Show every order' },
                  ...Object.values(henceofrthEnums.OrderStatus).map((s) => ({
                    value: s,
                    label: s.replace(/_/g, ' '),
                  })),
                ];

                return (
                  <SelectDropdown
                    heading="Order Status"
                    options={statusOptions}
                    value={activeValue}
                    onChange={(val) => {
                      if (val === henceofrthEnums.OrderStatus.REFUNDED) {
                        handleStatusFilter(val, true);
                      } else {
                        handleStatusFilter(val);
                      }
                    }}
                    placeholder="All Statuses"
                    align="left"
                  />
                );
              })()}

              {/* Date Filter Popover */}
              <div className="relative" ref={datePopoverRef}>
                <button
                  type="button"
                  onClick={() => setShowDatePopover(!showDatePopover)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm font-semibold transition-all cursor-pointer shadow-sm hover:shadow-md ${hasDate
                    ? 'border-slate-300 bg-white text-slate-800 shadow-md'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                    }`}
                >
                  <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                  <span>
                    {hasDate
                      ? `${moment(Number(newParam.get('start_date'))).format('DD MMM')} - ${moment(
                        Number(newParam.get('end_date'))
                      ).format('DD MMM')}`
                      : 'Date Range'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>

                {showDatePopover && (
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 z-50 animate-in fade-in zoom-in-95">
                    <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Filter by Date Range
                      </span>
                      {hasDate && (
                        <button
                          type="button"
                          onClick={handleClearDate}
                          className="text-xs text-[#1AA7DE] font-semibold hover:underline cursor-pointer"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <Calendar selectRange onChange={dateSelect} className="border-0 shadow-none" />
                  </div>
                )}
              </div>

              {/* Price Filter Popover */}
              <div className="relative" ref={pricePopoverRef}>
                <button
                  type="button"
                  onClick={() => setShowPricePopover(!showPricePopover)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm font-semibold transition-all cursor-pointer shadow-sm hover:shadow-md ${hasPrice
                    ? 'border-slate-300 bg-white text-slate-800 shadow-md'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                    }`}
                >
                  <DollarSign className="w-3.5 h-3.5 text-slate-400" />
                  <span>
                    {hasPrice
                      ? `$${newParam.get('min_price')} - $${newParam.get('max_price')}`
                      : 'Price Range'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>

                {showPricePopover && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 z-50 animate-in fade-in zoom-in-95">
                    <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Filter by Price
                      </span>
                      {hasPrice && (
                        <button
                          type="button"
                          onClick={handleClearPrice}
                          className="text-xs text-[#1AA7DE] font-semibold hover:underline cursor-pointer"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <form onSubmit={handleApplyPrice} className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[11px] font-bold text-slate-500 block mb-1">
                            Min ($)
                          </label>
                          <input
                            type="number"
                            value={minPriceInput}
                            onChange={(e) => setMinPriceInput(e.target.value)}
                            placeholder="0"
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-bold text-slate-500 block mb-1">
                            Max ($)
                          </label>
                          <input
                            type="number"
                            value={maxPriceInput}
                            onChange={(e) => setMaxPriceInput(e.target.value)}
                            placeholder="1000"
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                          />
                        </div>
                      </div>

                      {/* Quick presets */}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {[
                          { label: '$0 - $50', min: '0', max: '50' },
                          { label: '$50 - $200', min: '50', max: '200' },
                          { label: '$200 - $1000', min: '200', max: '1000' },
                        ].map((preset) => (
                          <button
                            key={preset.label}
                            type="button"
                            onClick={() => {
                              setMinPriceInput(preset.min);
                              setMaxPriceInput(preset.max);
                            }}
                            className="px-2 py-1 rounded text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                        <button
                          type="submit"
                          className="flex-1 py-1.5 rounded-lg bg-[#1AA7DE] hover:bg-[#158ebf] text-white font-bold text-xs shadow-2xs transition-colors cursor-pointer"
                        >
                          Apply Price
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowPricePopover(false)}
                          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-xs transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Reset All Filters button */}
              {isAnyFilterActive && (
                <button
                  type="button"
                  onClick={handleResetAllFilters}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-rose-200 text-sm font-semibold text-rose-500 bg-white shadow-sm hover:shadow-md hover:border-rose-300 transition-all cursor-pointer"
                  title="Reset all filters"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 3. AG Grid Modern Table */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <AgTable
            rowData={orderL?.data || []}
            columnDefs={columnDefs}
            loading={loading}
            pagination={true}
            page={Number(match?.params.page) || 1}
            limit={Number(Limit)}
            count={orderL?.total_count}
            emptyMessage={language_data?.[authState.lang]?.common_no_data_found || 'No Data Found'}
            emptySubtitle="Customer orders will automatically appear here once checkout transactions are created."
          />
        </div>
      </div>

      <DownloadFileModal exportData={exportData} />
    </Fragment>
  );
};

export default OrderListing;